import { defineStore } from 'pinia'
import { NETWORK } from '@shared/utils/constant'
import { injectivePeggyAddress } from '@shared/data/token'
import { getEthereumAddress } from '@injectivelabs/sdk-ts/utils'
import { getErc20BalancesAndAllowances } from '~/app/services/web3/fetchERC20BalanceAndAllowance'
import { lazyPiniaAction } from '@/utils/pinia'
import { walletNetworkMapping } from '@/app/data/wallet'
import {
  PEGGY_NETWORKS,
  COSMOS_NETWORKS,
  WORMHOLE_NETWORKS
} from '@/app/data/network'
// These don't use useAccountStore() so no circular dependency
import {
  fetchAddresses,
  fetchCosmosBalance,
  fetchCosmosAddress,
  fetchAddressFromWalletStrategy
} from '@/store/account/queries'
import type { Network } from '@shared/types'
import type { Coin } from '@injectivelabs/sdk-ts'
import type { Wallet } from '@injectivelabs/wallet-base'
import type { ERC20BalanceAndAllowance } from '@/types'

type AccountStoreState = {
  networkAddressMap: Record<Network, string>
  networkBalanceMap: Record<Network, Coin[]>
  networkAllowanceMap: Record<Network, Coin[]>
  ethereumBalances: ERC20BalanceAndAllowance[]
}

const initialStateFactory = (): AccountStoreState => ({
  networkBalanceMap: {} as Record<Network, Coin[]>,
  networkAddressMap: {} as Record<Network, string>,
  networkAllowanceMap: {} as Record<Network, Coin[]>,
  ethereumBalances: []
})

export const useAccountStore = defineStore('account', {
  state: (): AccountStoreState => initialStateFactory(),
  actions: {
    fetchAddresses,
    fetchCosmosAddress,
    fetchAddressFromWalletStrategy,
    // Uses useAccountStore() - needs lazy loading to avoid circular dependency
    fetchPeggyDenomBalance: lazyPiniaAction(
      () => import('./queries/eth'),
      'fetchPeggyDenomBalance'
    ),

    async fetchNetworkAddress(network: Network) {
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      if ([...PEGGY_NETWORKS].includes(network)) {
        accountStore.$patch({
          networkAddressMap: {
            ...accountStore.networkAddressMap,
            [network]: getEthereumAddress(sharedWalletStore.injectiveAddress)
          }
        })
      }

      if (COSMOS_NETWORKS.includes(network)) {
        const address = await fetchCosmosAddress({
          network,
          wallet: sharedWalletStore.wallet
        })

        if (address) {
          accountStore.$patch({
            networkAddressMap: {
              ...accountStore.networkAddressMap,
              [network]: address
            }
          })
        }
      }
    },

    async fetchNetworkBalance(network: Network) {
      const tokenStore = useTokenStore()
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (WORMHOLE_NETWORKS.includes(network)) {
        return
      }

      const networkAddress = accountStore.networkAddressMap[network]

      if (!sharedWalletStore.isUserConnected || !networkAddress) {
        return
      }

      if (PEGGY_NETWORKS.includes(network)) {
        const denoms = tokenStore.ethereumNetworkTokens.map(
          ({ denom }) => denom
        )

        await accountStore.fetchPeggyDenomBalance({
          denoms,
          address: sharedWalletStore.address
        })
      }

      if (COSMOS_NETWORKS.includes(network)) {
        const balances = await fetchCosmosBalance({
          network,
          address: networkAddress
        })

        if (balances) {
          accountStore.$patch({
            networkBalanceMap: {
              ...accountStore.networkBalanceMap,
              [network]: balances
            }
          })
        }
      }
    },

    async getWalletNetworkAddresses(wallet: Wallet) {
      const accountStore = useAccountStore()

      const networks = walletNetworkMapping[wallet] || PEGGY_NETWORKS

      await Promise.all(
        networks.map(
          async (network) =>
            await accountStore.fetchNetworkAddress(network as Network)
        )
      )
    },

    async fetchEthBalanceAndAllowance() {
      const sharedTokenStore = useTokenStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const peggyContractAddress = injectivePeggyAddress[NETWORK]

      const ethErcTokens = sharedTokenStore.ethereumNetworkTokens

      const balances = await getErc20BalancesAndAllowances({
        address: sharedWalletStore.address,
        spender: peggyContractAddress,
        tokenList: ethErcTokens
      })

      this.ethereumBalances = balances

      return balances
    },

    reset() {
      const accountStore = useAccountStore()

      accountStore.networkAddressMap = {} as Record<Network, string>
      accountStore.networkBalanceMap = {} as Record<Network, Coin[]>
    }
  }
})
